import { useMemo, useState } from 'react';

import { MonthPicker } from '@/components/layout/MonthPicker';
import { PageHeader } from '@/components/layout/PageHeader';
import { EntityLink } from '@/components/nav/EntityLink';
import { StateBlock } from '@/components/reports/StateBlock';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldRow } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Meter } from '@/components/ui/meter';
import { matchesQuery, SearchInput } from '@/components/ui/search-input';
import { Select } from '@/components/ui/select';
import { Stat, StatRow } from '@/components/ui/stat';
import { Table, TableWrap, Td, TdNum, Th } from '@/components/ui/table';
import { ApiError, type Assignment, api, type Member, type Project } from '@/lib/api';
import { hours, monthLabel, yenExact } from '@/lib/format';
import { toMember, toProject, useMonthParam, useQueryParam } from '@/lib/nav';
import { useApi } from '@/lib/useApi';

/**
 * アサイン計画。
 *
 * 予実管理の「予」を組む画面。実績 (MCP からの記録) はここでは触らない。
 * 粒度は「誰が・どのプロジェクトに・その月に何時間」まで。
 */
export function AssignmentsPage({ isAdmin }: { isAdmin: boolean }) {
  // 対象月と絞り込みは URL が持つ。稼働レポートから「この人の予定」を
  // 開いたとき、見ていた月とメンバーがそのまま渡ってくる。
  const [month, setMonth] = useMonthParam();
  const [query, setQuery] = useQueryParam();
  const [adding, setAdding] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const { data, error, loading, reload } = useApi<{ month: string; assignments: Assignment[] }>(
    `/api/assignments?month=${month}`,
  );
  const projects = useApi<{ projects: Project[] }>('/api/projects');
  // メンバー一覧は管理者しか読めない。閲覧だけの人には行を出さない。
  const members = useApi<{ users: Member[] }>(isAdmin ? '/api/users' : null);

  const all = data?.assignments ?? [];
  const rows = all.filter((row) => matchesQuery(query, row.userName, row.projectName));

  // その月にどこにも積まれていない人。計画を組むときに一番知りたいのがこれ
  // (稼働レポートは monthly_summary 由来なので、予定も実績も無い人は出てこない)。
  const assignedUserIds = new Set(all.map((row) => row.userId));
  const unassigned = (members.data?.users ?? []).filter(
    (member) => member.status === 'active' && !assignedUserIds.has(member.id),
  );
  // 合計は絞り込みに追随させる (「この人だけ何時間積んでいるか」を見るため)
  const totals = useMemo(
    () => ({
      planned: rows.reduce((sum, row) => sum + row.plannedHours, 0),
      actual: rows.reduce((sum, row) => sum + row.actualHours, 0),
    }),
    [rows],
  );

  async function copyPreviousMonth() {
    setMessage(null);
    try {
      const result = await api.post<{ from: string; copied: number; skipped: number }>(
        '/api/assignments/copy',
        { to: month },
      );
      setMessage(
        result.copied === 0 && result.skipped === 0
          ? `${monthLabel(result.from)}にアサインがありません`
          : `${monthLabel(result.from)}から ${result.copied} 件を複製しました` +
              (result.skipped > 0 ? ` (既存 ${result.skipped} 件はそのまま)` : ''),
      );
      reload();
    } catch (cause) {
      setMessage(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    }
  }

  return (
    <>
      <PageHeader
        title="アサイン計画"
        subtitle={
          query.trim() === ''
            ? `${monthLabel(month)} / ${all.length} 件`
            : `${monthLabel(month)} / ${rows.length} / ${all.length} 件`
        }
        actions={
          <>
            <MonthPicker month={month} onChange={setMonth} />
            {isAdmin ? (
              <>
                <Button onClick={copyPreviousMonth}>前月から複製</Button>
                <Button variant="primary" onClick={() => setAdding((value) => !value)}>
                  {adding ? '閉じる' : 'アサインを追加'}
                </Button>
              </>
            ) : null}
          </>
        }
      />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        {message ? <p className="text-xs text-ink-muted">{message}</p> : null}

        {adding ? (
          <CreateForm
            month={month}
            projects={projects.data?.projects ?? []}
            members={members.data?.users ?? []}
            onCreated={() => {
              setAdding(false);
              reload();
            }}
          />
        ) : null}

        {isAdmin && unassigned.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border border-line-bold bg-surface-raised px-6 py-4">
            <span className="text-xs font-bold">{monthLabel(month)}に予定が無いメンバー</span>
            {unassigned.map((member) => (
              <EntityLink key={member.id} target={toMember(member)} className="text-xs">
                {member.name}
              </EntityLink>
            ))}
          </div>
        ) : null}

        <StateBlock
          loading={loading}
          error={error}
          empty={all.length === 0}
          emptyMessage={`${monthLabel(month)}のアサインはまだありません。前月から複製するか、手で追加してください。`}
        >
          <StatRow className="grid-cols-1 [&>*+*]:border-t md:grid-cols-3 md:[&>*+*]:border-t-0">
            <Stat label="予定合計" value={hours(totals.planned)} />
            <Stat label="実績合計" value={hours(totals.actual)} tone="brand" />
            <Stat label="残り" value={hours(Math.max(0, totals.planned - totals.actual))} />
          </StatRow>

          <div className="border border-line bg-surface-raised">
            <div className="border-b border-line px-4 py-3">
              <SearchInput
                value={query}
                onChange={setQuery}
                placeholder="メンバー・プロジェクトで絞り込む"
                className="w-full md:w-80"
              />
            </div>
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>MEMBER</Th>
                    <Th>PROJECT</Th>
                    <Th className="md:w-[22%]">PLANNED / ACTUAL</Th>
                    <Th className="text-right">PLANNED</Th>
                    <Th className="text-right">ACTUAL</Th>
                    <Th className="text-right">CUSTOM COST</Th>
                    {isAdmin ? <Th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <AssignmentRow key={row.id} row={row} isAdmin={isAdmin} onSaved={reload} />
                  ))}
                </tbody>
              </Table>
            </TableWrap>
            {rows.length === 0 ? (
              <p className="border-t border-line px-4 py-8 text-sm text-ink-subtle">
                「{query}」に一致するアサインはありません。
              </p>
            ) : null}
          </div>
        </StateBlock>

        <p className="text-xs leading-relaxed text-ink-subtle">
          実績はエンジニアが AI クライアント (MCP) から記録したものです。この画面では変更しません。
          アサインを消しても記録済みの工数は残ります（締めた月の数字を後から動かさないため）。
          特別原価単価を空にすると、個人 → テナントの既定単価に戻ります。
        </p>
      </div>
    </>
  );
}

function CreateForm({
  month,
  projects,
  members,
  onCreated,
}: {
  month: string;
  projects: Project[];
  members: Member[];
  onCreated: () => void;
}) {
  const selectableProjects = projects.filter((project) => project.status === 'active');
  const selectableMembers = members.filter((member) => member.status !== 'disabled');

  const [projectId, setProjectId] = useState('');
  const [userId, setUserId] = useState('');
  const [plannedHours, setPlannedHours] = useState('');
  const [customHourlyCost, setCustomHourlyCost] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/assignments', {
        projectId,
        userId,
        month,
        plannedHours: Number(plannedHours),
        customHourlyCost: customHourlyCost === '' ? null : Number(customHourlyCost),
        note,
      });
      setPlannedHours('');
      setCustomHourlyCost('');
      setNote('');
      onCreated();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const ready = projectId !== '' && userId !== '' && plannedHours !== '';

  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
      <h2 className="text-sm font-bold">{monthLabel(month)}のアサインを追加する</h2>
      <FieldRow>
        <Field label="メンバー" className="min-w-48">
          <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">選択してください</option>
            {selectableMembers.map((member) => (
              <option key={member.id} value={member.id}>
                {member.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="プロジェクト" className="min-w-56">
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">選択してください</option>
            {selectableProjects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="予定工数 (h)" className="w-32">
          <Input
            type="number"
            min={0}
            step={0.25}
            value={plannedHours}
            onChange={(e) => setPlannedHours(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field label="特別原価単価 (円/時)" className="w-44" hint="空欄なら既定の単価">
          <Input
            type="number"
            min={0}
            step={100}
            value={customHourlyCost}
            onChange={(e) => setCustomHourlyCost(e.target.value)}
            className="font-mono"
          />
        </Field>
        <Field label="メモ" className="min-w-48 flex-1">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button variant="primary" onClick={submit} disabled={busy || !ready}>
          追加
        </Button>
      </FieldRow>
      {error ? <p className="text-sm text-alert">{error}</p> : null}
    </div>
  );
}

function AssignmentRow({
  row,
  isAdmin,
  onSaved,
}: {
  row: Assignment;
  isAdmin: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [plannedHours, setPlannedHours] = useState(String(row.plannedHours));
  const [customHourlyCost, setCustomHourlyCost] = useState(
    row.customHourlyCost === null ? '' : String(row.customHourlyCost),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ratio = row.plannedHours > 0 ? Math.min(1, row.actualHours / row.plannedHours) : 0;
  const over = row.plannedHours > 0 && row.actualHours > row.plannedHours;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setEditing(false);
      onSaved();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '通信に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <tr>
        <Td>
          <div className="flex items-center gap-2">
            <EntityLink
              target={toMember({ id: row.userId, name: row.userName })}
              className="font-medium"
            >
              {row.userName}
            </EntityLink>
            {row.userStatus === 'disabled' ? <Badge variant="outline">無効</Badge> : null}
          </div>
        </Td>
        <Td className="text-ink-muted">
          <div className="flex items-center gap-2">
            <EntityLink target={toProject({ id: row.projectId, name: row.projectName })}>
              {row.projectName}
            </EntityLink>
            {row.projectStatus === 'archived' ? <Badge variant="outline">ARCHIVED</Badge> : null}
          </div>
        </Td>
        <Td>
          <Meter
            segments={[{ ratio, className: over ? 'bg-alert' : 'bg-brand-500', label: '実績' }]}
          />
        </Td>
        <TdNum>{row.plannedHours} h</TdNum>
        <TdNum className={over ? 'text-alert' : undefined}>{row.actualHours} h</TdNum>
        <TdNum className="text-ink-subtle">
          {row.customHourlyCost === null ? '—' : yenExact(row.customHourlyCost)}
        </TdNum>
        {isAdmin ? (
          <Td className="text-right">
            <Button onClick={() => setEditing((value) => !value)} aria-expanded={editing}>
              {editing ? '閉じる' : '編集'}
            </Button>
          </Td>
        ) : null}
      </tr>

      {editing ? (
        <tr>
          <td colSpan={7} className="border-t border-line bg-surface px-6 py-5">
            <FieldRow>
              <Field label="予定工数 (h)" className="w-32">
                <Input
                  type="number"
                  min={0}
                  step={0.25}
                  value={plannedHours}
                  onChange={(e) => setPlannedHours(e.target.value)}
                  className="font-mono"
                  disabled={busy}
                />
              </Field>
              <Field label="特別原価単価 (円/時)" className="w-44" hint="空欄で既定の単価に戻す">
                <Input
                  type="number"
                  min={0}
                  step={100}
                  value={customHourlyCost}
                  onChange={(e) => setCustomHourlyCost(e.target.value)}
                  className="font-mono"
                  disabled={busy}
                />
              </Field>
              <Button
                variant="primary"
                disabled={busy}
                onClick={() =>
                  run(() =>
                    api.patch(`/api/assignments/${row.id}`, {
                      plannedHours: Number(plannedHours),
                      customHourlyCost: customHourlyCost === '' ? null : Number(customHourlyCost),
                    }),
                  )
                }
              >
                保存
              </Button>
              <Button
                variant="ghost"
                disabled={busy}
                onClick={() => run(() => api.delete(`/api/assignments/${row.id}`))}
              >
                アサインを削除
              </Button>
            </FieldRow>
            {row.note ? <p className="pt-4 text-xs text-ink-muted">{row.note}</p> : null}
            {error ? <p className="pt-2 text-sm text-alert">{error}</p> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}
